import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import {
  audienceGroups as audienceGroupsApi,
  categories as categoriesApi,
} from "shared/api";
import CircleBadge, { MEMBERSHIP_BADGES } from "../CircleBadge";
import TreeRails, { withTreeGuides } from "BSM/components/TreeRails";
import TagsItem from "../TagsItem";
import CollapseToggle from "../CollapseToggle";
import styles from "./ContentSettingsCard.module.css";

// Audience pin tiers per docs/screens-and-flows.md "Audience pins" — normalized to EME.
const DEFAULT_MEMBERSHIP_OPTIONS = [
  { id: "C", label: "C" },
  { id: "P", label: "P" },
  { id: "IBO", label: "IBO" },
  { id: "PLA", label: "PLA" },
  { id: "EME", label: "EME" },
  { id: "DIA", label: "DIA" },
];

const DEFAULT_PIN_LEVEL_OPTIONS = [
  { id: "IBO", label: "IBO" },
  { id: "PLA", label: "PLA" },
  { id: "EME", label: "EME" },
  { id: "DIA", label: "DIA" },
];

// WWG Store App Categories — store-facing categories authored in the Category
// Builder app and landed in Mongo as the `Categories` reference collection
// (same source `krsToProduct` reads). Fetched live from GET /v1/categories so
// the codes offered here match the `storeAppCategories` codes a product carries
// from NetSuite. There is deliberately NO placeholder/fake fallback: if the tree
// can't be loaded we show an explicit "not set up" notice rather than fake chips
// that look real but don't map to anything.

// Depth-first flatten of the category tree preserving order + level so the
// selector can render one selectable chip per node, indented by depth.
// `ancestors` carries every code above a node so selection can keep a branch
// whole — a subcategory is meaningless without the categories it sits under.
export function flattenCategoryTree(nodes, out = [], ancestors = []) {
  for (const node of nodes || []) {
    if (!node || !node.code) continue;
    out.push({
      code: node.code,
      title: node.title || node.code,
      level: node.level || 1,
      ancestors,
    });
    if (node.children && node.children.length) {
      flattenCategoryTree(node.children, out, [...ancestors, node.code]);
    }
  }
  return out;
}

// Selecting a category selects the whole branch above it, and clearing one
// clears the branch below it, so a product can never be filed under
// "Motivational" without also being under "Books" and "Literature".
export function toggleCategoryBranch(selected, node, allNodes) {
  const set = new Set(selected || []);
  if (set.has(node.code)) {
    set.delete(node.code);
    for (const other of allNodes) {
      if ((other.ancestors || []).includes(node.code)) set.delete(other.code);
    }
  } else {
    set.add(node.code);
    for (const ancestor of node.ancestors || []) set.add(ancestor);
  }
  return Array.from(set);
}

function toggleInArray(arr, id) {
  const set = new Set(arr || []);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return Array.from(set);
}

const CollapsibleBar = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={styles.collapsibleSection}>
      <button
        type="button"
        className={`${styles.sectionTitle} ${
          open ? styles.sectionTitleOpen : ""
        }`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={open ? "Collapse this section" : "Expand this section"}
      >
        <span className={styles.sectionTitleText}>{title}</span>
        <span className={styles.sectionChevron} aria-hidden="true">
          ›
        </span>
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
};

const ContentSettingsCard = ({
  membershipOptions,
  selectedMemberships,
  onMembershipChange,
  pinLevelOptions,
  selectedPinLevels,
  onPinLevelChange,
  selectedFamilyGroupIds,
  onFamilyGroupsChange,
  selectedDiamondIds,
  onDiamondsChange,
  additionalIbos,
  onAdditionalIbosChange,
  selectedTagIds,
  onTagsChange,
  selectedAppCategories,
  onAppCategoriesChange,
}) => {
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [iboInput, setIboInput] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setGroupsLoading(true);
    setGroupsError(null);
    audienceGroupsApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setGroupsError(err);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    categoriesApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setCategoryTree(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setCategoryTree([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only ever offer the live Category Builder tree. There is no fake fallback:
  // if the fetch fails or returns nothing, renderCategoryChips shows a "not set
  // up" notice instead of chips.
  const categoryNodes = useMemo(
    () => flattenCategoryTree(categoryTree),
    [categoryTree]
  );

  const familyGroups = useMemo(
    () => groups.filter((g) => g.category === "family_reunion"),
    [groups]
  );
  const diamondGroups = useMemo(
    () => groups.filter((g) => g.category === "diamond"),
    [groups]
  );

  const handleAddIbo = useCallback(() => {
    const trimmed = iboInput.trim();
    if (!trimmed || !onAdditionalIbosChange) return;
    if ((additionalIbos || []).includes(trimmed)) {
      setIboInput("");
      return;
    }
    onAdditionalIbosChange([...(additionalIbos || []), trimmed]);
    setIboInput("");
  }, [iboInput, additionalIbos, onAdditionalIbosChange]);

  const handleRemoveIbo = (ibo) => {
    if (!onAdditionalIbosChange) return;
    onAdditionalIbosChange((additionalIbos || []).filter((i) => i !== ibo));
  };

  const renderPinChips = (options, selected, onChange, useBadge = false) => (
    <div className={styles.pinRow}>
      {options.map((opt) => {
        const active = (selected || []).includes(opt.id);
        const badge = useBadge ? MEMBERSHIP_BADGES[opt.id] : null;
        // "Select PLA" tells you nothing you can't already read on the chip, so
        // the tooltip spells the tier out the way the store card does.
        const fullName =
          (MEMBERSHIP_BADGES[opt.id] && MEMBERSHIP_BADGES[opt.id].title) ||
          opt.label;
        return (
          <button
            type="button"
            key={opt.id}
            className={`${styles.pinChip} ${
              active ? styles.pinChipActive : ""
            } ${badge ? styles.pinChipBadge : ""}`}
            onClick={() =>
              onChange && onChange(toggleInArray(selected, opt.id))
            }
            aria-pressed={active}
            aria-label={fullName}
            title={`${active ? "Deselect" : "Select"} ${fullName}`}
          >
            {badge ? (
              <CircleBadge
                label={badge.label}
                // A permission that is switched off empties out rather than
                // wearing its tier colour, so the row reads at a glance.
                bgColor={active ? badge.bgColor : "#ffffff"}
                textColor={active ? badge.textColor : "#000000"}
                size="large"
                highlighted={active}
              />
            ) : (
              opt.label
            )}
          </button>
        );
      })}
    </div>
  );

  const renderCategoryTree = () => {
    if (categoriesLoading && !categoryNodes.length) {
      return <div className={styles.helperText}>Loading…</div>;
    }
    // No live tree available (fetch failed, endpoint not wired up, or the
    // Category Builder collection is empty). Show an explicit "not set up"
    // notice instead of placeholder chips that don't map to real categories.
    if (!categoryNodes.length) {
      return (
        <div className={styles.categoryNotice} role="status">
          <span className={styles.categoryNoticeTitle}>
            Categories aren’t set up yet
          </span>
          <span className={styles.helperText}>
            WWG Store App categories come from the Category Builder. That
            connection isn’t available in this environment yet, so categories
            can’t be assigned here until it’s set up.
          </span>
        </div>
      );
    }
    // Nested checkbox tree (parent > category > subcategory). Uses the same
    // connector rails as the Category filter in the product list rather than
    // indentation alone: this is the same tree, with the same reused sibling
    // titles ("Bundles" appears under four different parents), so depth on its
    // own doesn't say which parent a row belongs to.
    const rows = withTreeGuides(
      categoryNodes.map((node) => ({ ...node, depth: node.level || 1 }))
    );
    return (
      <>
        <div className={styles.categoryTree}>
          {rows.map((node) => {
            const checked = (selectedAppCategories || []).includes(node.code);
            return (
              <div key={node.code} className={styles.categoryRow}>
                <TreeRails
                  depth={node.depth}
                  ancestorContinues={node.ancestorContinues}
                  isLastChild={node.isLastChild}
                />
                <CheckboxGroup
                  id={`cs-cat-${node.code}`}
                  checked={checked}
                  onChange={() =>
                    onAppCategoriesChange &&
                    onAppCategoriesChange(
                      toggleCategoryBranch(
                        selectedAppCategories,
                        node,
                        categoryNodes
                      )
                    )
                  }
                >
                  {node.title}
                </CheckboxGroup>
              </div>
            );
          })}
        </div>
        {/*
          Same shape as the audience warning below. The codes shown here do
          match the storefront's real assignments in Store.ProductCategories
          (verified against DEV: all 11 SQL-backed SKUs agree), but nothing
          writes a change back to that table — so a new selection is stored
          intent, not a live re-categorisation.
        */}
      </>
    );
  };

  // Group labels carry the real member count from Common.GroupMembers - the
  // same table the storefront's visibility filter joins - so an admin can see
  // how many people an audience actually covers before choosing it.
  const formatCount = (n) =>
    typeof n === "number" ? n.toLocaleString("en-US") : null;

  const renderGroupCheckboxes = (sectionKey, list, selected, onChange) => {
    if (groupsLoading) {
      return <div className={styles.helperText}>Loading…</div>;
    }
    if (groupsError) {
      return (
        <div className={styles.errorText}>Couldn’t load audience groups.</div>
      );
    }
    if (!list.length) {
      return <div className={styles.helperText}>No options available.</div>;
    }
    return (
      <div className={styles.checkboxGrid}>
        {list.map((g) => {
          const id = `cs-${sectionKey}-${g.id}`;
          const checked = (selected || []).includes(g.id);
          const count = formatCount(g.iboCount);
          // A group with no members does NOT hide the product from everyone -
          // the storefront treats a rule whose groups are all empty as open.
          // Saying so is the whole point; "0" on its own reads as "nobody".
          const isOpen = g.restrictsToday === false;
          return (
            <CheckboxGroup
              key={g.id}
              id={id}
              checked={checked}
              onChange={() =>
                onChange && onChange(toggleInArray(selected, g.id))
              }
            >
              <span className={styles.groupLabel}>
                <span className={styles.groupName}>{g.name}</span>
                {count !== null && (
                  <span
                    className={`${styles.groupCount}${
                      isOpen ? ` ${styles.groupCountEmpty}` : ""
                    }`}
                    title={
                      isOpen
                        ? "This group has no members yet, so it does not restrict who can see the product."
                        : `${count} IBOs are in this group`
                    }
                  >
                    {isOpen ? "no members yet" : `${count} IBOs`}
                  </span>
                )}
              </span>
            </CheckboxGroup>
          );
        })}
      </div>
    );
  };

  return (
    <Card
      className={`${styles.card}${collapsed ? ` ${styles.cardCollapsed}` : ""}`}
    >
      <div
        className={`${styles.headerRow}${
          collapsed ? ` ${styles.headerRowCollapsed}` : ""
        }`}
      >
        <span className={styles.title} role="heading" aria-level="3">
          Content Settings
        </span>
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />
      </div>

      {!collapsed && (
        <>
          <div className={styles.permissionsRow}>
            <div className={styles.permissionBlock}>
              <span className={styles.sectionLabel}>
                Membership Permissions
              </span>
              {renderPinChips(
                membershipOptions,
                selectedMemberships,
                onMembershipChange,
                true
              )}
            </div>
            <div className={styles.permissionBlock}>
              <span className={styles.sectionLabel}>Pin Level Permissions</span>
              {renderPinChips(
                pinLevelOptions,
                selectedPinLevels,
                onPinLevelChange,
                true
              )}
            </div>
          </div>

          <CollapsibleBar
            title={
              <>
                Family Reunion Diamond Groups
                <span className={styles.sectionHint}>
                  {" "}
                  (Will only show to these diamonds and their LOS)
                </span>
              </>
            }
          >
            {renderGroupCheckboxes(
              "fr",
              familyGroups,
              selectedFamilyGroupIds,
              onFamilyGroupsChange
            )}
          </CollapsibleBar>

          <CollapsibleBar
            title={
              <>
                Diamonds
                <span className={styles.sectionHint}>
                  {" "}
                  (Will only show to these diamonds and their LOS)
                </span>
              </>
            }
          >
            {renderGroupCheckboxes(
              "dia",
              diamondGroups,
              selectedDiamondIds,
              onDiamondsChange
            )}
          </CollapsibleBar>

          <CollapsibleBar
            title={
              <>
                Additional IBOs
                <span className={styles.sectionHint}>
                  {" "}
                  (These IBOs will be added to view content outside of their pin
                  level)
                </span>
              </>
            }
          >
            <div className={styles.iboInputRow}>
              <input
                className={styles.textInput}
                placeholder="IBO #"
                inputMode="numeric"
                value={iboInput}
                onChange={(e) =>
                  // Strip non-digits and cap at 12 in the handler (not via maxLength,
                  // which counts raw chars and would truncate a pasted value with any
                  // non-digit prefix before the strip runs, silently dropping digits).
                  setIboInput(e.target.value.replace(/\D/g, "").slice(0, 12))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddIbo();
                  }
                }}
              />
              <button
                type="button"
                className={styles.addButton}
                onClick={handleAddIbo}
                disabled={!iboInput.trim()}
                title="Add the entered IBO number so they can view this content outside their pin level."
              >
                + Add
              </button>
            </div>
            {(additionalIbos || []).length > 0 && (
              <div className={styles.iboList}>
                {(additionalIbos || []).map((ibo) => (
                  <span key={ibo} className={styles.iboChip}>
                    <span className={styles.iboChipLabel}>{ibo}</span>
                    <button
                      type="button"
                      className={styles.iboRemove}
                      aria-label={`Remove ${ibo}`}
                      onClick={() => handleRemoveIbo(ibo)}
                      title={`Remove IBO ${ibo} from this list.`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </CollapsibleBar>

          <CollapsibleBar title="WWG Store App Categories">
            {renderCategoryTree()}
          </CollapsibleBar>

          <CollapsibleBar title="Tags">
            <TagsItem
              title=""
              selectedTags={selectedTagIds}
              onChange={onTagsChange}
            />
          </CollapsibleBar>
        </>
      )}
    </Card>
  );
};

ContentSettingsCard.propTypes = {
  membershipOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedMemberships: PropTypes.arrayOf(PropTypes.string),
  onMembershipChange: PropTypes.func,
  pinLevelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedPinLevels: PropTypes.arrayOf(PropTypes.string),
  onPinLevelChange: PropTypes.func,
  selectedFamilyGroupIds: PropTypes.arrayOf(PropTypes.string),
  onFamilyGroupsChange: PropTypes.func,
  selectedDiamondIds: PropTypes.arrayOf(PropTypes.string),
  onDiamondsChange: PropTypes.func,
  additionalIbos: PropTypes.arrayOf(PropTypes.string),
  onAdditionalIbosChange: PropTypes.func,
  selectedTagIds: PropTypes.arrayOf(PropTypes.string),
  onTagsChange: PropTypes.func,
  selectedAppCategories: PropTypes.arrayOf(PropTypes.string),
  onAppCategoriesChange: PropTypes.func,
};

ContentSettingsCard.defaultProps = {
  membershipOptions: DEFAULT_MEMBERSHIP_OPTIONS,
  selectedMemberships: [],
  onMembershipChange: undefined,
  pinLevelOptions: DEFAULT_PIN_LEVEL_OPTIONS,
  selectedPinLevels: [],
  onPinLevelChange: undefined,
  selectedFamilyGroupIds: [],
  onFamilyGroupsChange: undefined,
  selectedDiamondIds: [],
  onDiamondsChange: undefined,
  additionalIbos: [],
  onAdditionalIbosChange: undefined,
  selectedTagIds: [],
  onTagsChange: undefined,
  selectedAppCategories: [],
  onAppCategoriesChange: undefined,
};

export default ContentSettingsCard;
